import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, GripVertical, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Super-admin only. Reorders the global left-sidebar module order.
 * `items` is the full canonical nav list (already in current saved order).
 */
export const SidebarReorderDialog = ({ open, onOpenChange, items, onSaved }) => {
  const [list, setList] = useState([]);
  const [saving, setSaving] = useState(false);
  const dragIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    if (open) setList(items.map((i) => ({ to: i.to, label: i.label, icon: i.icon })));
  }, [open, items]);

  const move = (from, to) => {
    if (to < 0 || to >= list.length || from === to) return;
    setList((arr) => {
      const next = [...arr];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const onDrop = (i) => {
    if (dragIdx.current !== null) move(dragIdx.current, i);
    dragIdx.current = null;
    setOverIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/sidebar-order", { order: list.map((i) => i.to) });
      toast.success("Sidebar order saved for everyone");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save order");
    } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    setSaving(true);
    try {
      await api.put("/sidebar-order", { order: [] });
      toast.success("Reset to default order");
      onOpenChange(false);
      onSaved?.();
    } catch { toast.error("Could not reset"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sidebar-reorder-dialog">
        <DialogHeader>
          <DialogTitle>Reorder sidebar</DialogTitle>
          <DialogDescription>Drag the handle (or use the arrows) to set the order everyone sees. Each person still only sees modules they have access to.</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-1">
          {list.map((it, i) => (
            <div
              key={it.to}
              draggable
              onDragStart={() => { dragIdx.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { dragIdx.current = null; setOverIdx(null); }}
              className={`flex items-center gap-2 px-2 py-2 rounded-md border bg-white ${overIdx === i ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}
              data-testid={`reorder-item-${it.to.replace(/\//g, "")}`}
            >
              <GripVertical className="w-4 h-4 text-zinc-400 cursor-grab shrink-0" />
              {it.icon && <it.icon className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={1.5} />}
              <span className="text-sm flex-1 truncate">{it.label}</span>
              <span className="text-[10px] text-zinc-400 w-5 text-right">{i + 1}</span>
              <div className="flex flex-col">
                <button onClick={() => move(i, i - 1)} disabled={i === 0} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30" data-testid={`reorder-up-${i}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, i + 1)} disabled={i === list.length - 1} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30" data-testid={`reorder-down-${i}`}><ChevronDown className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <Button variant="ghost" onClick={resetDefault} disabled={saving} className="text-zinc-500" data-testid="reorder-reset"><RotateCcw className="w-4 h-4 mr-1" /> Reset to default</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-zinc-950" data-testid="reorder-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save order
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
